import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, Injector, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmdialogComponent } from '@shared/components/confirmdialog/confirmdialog.component';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { aciesDateFormat, getUpdatedByFromMeta } from '@shared/helpers';
import { formatZipCodeToXLSX } from '@shared/mappers/zipCodeMapper';
import { GenericTableColumn, ISelectableData } from '@shared/models/generictablecolumn';
import { ZipCodeValidatorService } from '@shared/services/filevalidators/zipCodevalidator.service';

import { NotificationService } from '@shared/services/notification.service';
import { IZipCode, PalletisingService } from '@shared/services/palletising.service';
import { UserService } from '@shared/services/user.service';
import { SelectSubMenuItem } from '@shared/utils/SelectSubMenuItemManager';
import { catchError, Subject, takeUntil, throwError } from 'rxjs';
import * as XLSX from 'xlsx';
import { CreateUpdateZipCodeDialogComponent } from './create-update-zipcode/create-update-zipcode';

interface IZipCodeAction {
	title: string;
	handler: 'editZipCode' | 'deleteZipCode';
}

interface IZipCodeRowAction {
	action: IZipCodeAction;
	data: ISelectableData<IZipCode> | ISelectableData<IZipCode>[];
}

@Component({
	selector: 'app-zip-code',
	templateUrl: './zipcode.component.html',
	styleUrl: './zipcode.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ZipCodeComponent implements OnInit, OnDestroy {
	public tableSource = new MatTableDataSource<ISelectableData<IZipCode>>();
	// public isLoading: boolean = false;
	public ZipCodesFilter: string = '';
	public countSubject$: Subject<number> = new Subject<number>();
	public count$ = this.countSubject$.asObservable();
	public unsubscribe: Subject<void> = new Subject<void>();
	public isLoading: Subject<boolean> = new Subject<boolean>();
	public userNamesLookup: Map<string, string> = new Map();
	public resetSelectionEvent: EventEmitter<void> = new EventEmitter<void>();

	public registerTableColumns: GenericTableColumn[] = [
		{
			name: 'masterdata.palletising.zipCodeRules.ruleNumber.name',
			dataKey: 'ruleNumber',
		},
		{
			name: 'masterdata.palletising.zipCodeRules.description.name',
			dataKey: 'description',
		},
		{
			name: 'masterdata.palletising.zipCodeRules.from.name',
			dataKey: 'from',
		},
		{ name: 'masterdata.palletising.zipCodeRules.to.name', dataKey: 'to' },
		{ name: 'masterdata.updatedDate', dataKey: 'updatedDate', sortKey: 'updatedAtEpoch' },
		{ name: 'masterdata.updatedBy', dataKey: 'updatedBy', sortAsShown: true },
	];

	public regRowActions: IZipCodeAction[] = [
		{ title: 'masterdata.edit', handler: 'editZipCode' },
		{ title: 'masterdata.delete', handler: 'deleteZipCode' },
	];

	public selectActions: SelectSubMenuItem<ISelectableData<IZipCode>>[] = [
		{
			icon: () => 'acies_upload',
			text: () => 'masterdata.palletising.zipCodeRules.export.button',
			click: (data) => this.handleExportZipCode(data),
		},
	];

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_download',
			text: () => 'masterdata.symbols.import.button',
			click: () => this.handleImportZipCode(),
		},
		{
			icon: () => 'acies_add',
			text: () => 'masterdata.palletising.newbtn.zipCodeRules',
			click: () => this.openCreateUpdateGrouping(),
		},
	];

	public constructor(
		public dialog: MatDialog,
		private injector: Injector,
		private userService: UserService,
		private translator: TranslateService,
		private palletisingService: PalletisingService,
		private notificationService: NotificationService,
	) {}

	public ngOnInit(): void {
		this.isLoading.next(true);
		this.loadData();
	}

	private loadData(): void {
		this.userService
			.fetchUsers()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe({
				next: (users) => {
					this.userNamesLookup = new Map(users.map((user) => [user.id, user.name]));
					this.updateZipCodes();
					this.isLoading.next(false);
				},
				error: () => {
					this.isLoading.next(false);
					this.notificationService.notify(['masterdata.palletising.errors.network'], [], 'error');
				},
			});
	}

	updateZipCodes() {
		this.isLoading.next(true);
		this.palletisingService
			.fetchZipCodes()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe({
				next: (value) => {
					this.countSubject$.next(value.items.length);

					const data = value.items.map((zipCode) => ({
						...zipCode,
						updatedDate: aciesDateFormat(new Date(zipCode.meta.updatedAt), true, false),
						updatedAtEpoch: zipCode.meta.updatedAt,
						updatedBy: this.userNamesLookup ? getUpdatedByFromMeta(this.userNamesLookup, zipCode.meta) : 'unknown',
						id: zipCode.self.split('/').pop(),
					}));

					this.tableSource.data = data;
					this.resetSelectionEvent.emit();
					this.isLoading.next(false);
				},
				error: (error: Error) => {
					console.error(error);
					this.notificationService.notify(['masterdata.palletising.errors.network'], [], 'error');
					this.isLoading.next(false);
				},
			});
	}

	public openCreateUpdateGrouping(data: IZipCode = null): void {
		this.dialog
			.open(CreateUpdateZipCodeDialogComponent, {
				disableClose: true,
				width: '650px',
				data,
			})
			.componentInstance.saved.pipe(takeUntil(this.unsubscribe))
			.subscribe(() => {
				this.loadData();
			});
	}

	handleImportZipCode() {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'masterdata.palletising.zipCodeRules.import.title',
				subHeadingKey: 'masterdata.palletising.zipCodeRules.import.subtitle',
				confirmButtonKey: 'masterdata.palletising.zipCodeRules.import.button',
				notificationHeader: 'masterdata.palletising.zipCodeRules.import.notification.success.header',
				notificationContent: 'masterdata.palletising.zipCodeRules.import.notification.success.content',
				notificationErrorHeader: 'masterdata.palletising.zipCodeRules.import.notification.error.header',
				notificationErrorContent: 'masterdata.palletising.zipCodeRules.import.notification.error.content',
				notificationClientErrorHeader: 'masterdata.palletising.zipCodeRules.import.notification.clientError.header',
				notificationClientErrorContent: 'masterdata.palletising.zipCodeRules.import.notification.clientError.content',
				detailedClientErrorMessage: 'masterdata.palletising.zipCodeRules.import.detailed-client-error-message',
				allowedFileTypes: ['.xlsx'],
				validatorService: this.injector.get(ZipCodeValidatorService),
				data: this.tableSource.data,
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(() => {
				this.loadData();
			});
	}

	handleRowMenuAction(object: object): void {
		const row = object as IZipCodeRowAction;

		switch (row.action.handler) {
			case 'editZipCode':
				this.openCreateUpdateGrouping(row.data as ISelectableData<IZipCode>);
				break;
			case 'deleteZipCode':
				this.onDeleteGrouping(row.data as ISelectableData<IZipCode>);
				break;
			// case 'exportZipCodes':
			// 	this.handleExportZipCode(row.data as ISelectableData<IZipCode>[]);
			// 	break;
		}
	}

	handleExportZipCode(data: ISelectableData<IZipCode>[]): void {
		const wb = formatZipCodeToXLSX(data);

		XLSX.writeFile(wb, 'zipCodes.xlsx');
	}

	onDeleteGrouping(zipCode: IZipCode) {
		this.dialog
			.open(ConfirmdialogComponent, {
				width: '450px',
				data: {
					okReturnData: true,
					cancelReturnData: false,
					heading: this.translator.instant('masterdata.palletising.zipCodeRules.delete.heading'),
					content: this.translator.instant('masterdata.palletising.zipCodeRules.delete.content', { id: zipCode.ruleNumber }),
					okButtonText: this.translator.instant('general.ok'),
					cancelButtonText: this.translator.instant('general.cancel'),
				},
			})
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((result) => {
				if (result) {
					this.palletisingService
						.deleteZipCode(zipCode)
						.pipe(catchError(this.handleError))
						.subscribe({
							next: () => {
								this.notificationService.notify(['masterdata.palletising.zipCodeRules.delete.notification.header'], ['masterdata.palletising.zipCodeRules.delete.notification.content', { id: zipCode.ruleNumber }], 'success');
								this.loadData();
							},
							error: (error: Error) => {
								this.notificationService.notify([error.message], [], 'error');
							},
						});
				}
			});
	}

	private handleError(error: HttpErrorResponse) {
		if (error.status === 0) {
			return throwError(() => new Error('masterdata.palletising.errors.network'));
		}
		return throwError(() => new Error('masterdata.palletising.errors.unknown'));
	}

	public ngOnDestroy() {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}
}
